import { useState } from "react";
import { 
  Activity, 
  User,
  Watch,
  AlertTriangle,
  Bell,
  Search,
  Filter,
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PatientVitalsCard } from "@/components/monitoring/PatientVitalsCard";
import { VitalsTrendChart } from "@/components/monitoring/VitalsTrendChart";
import { AlertsList } from "@/components/monitoring/AlertsList";
import { DeviceStatusCard } from "@/components/monitoring/DeviceStatusCard";
import { useMonitoredPatients, useHealthAlerts } from "@/hooks/useRemoteMonitoring";
import { useRpmReviewTimer } from "@/hooks/useRpmReviewTimer";

export default function RemoteMonitoring() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);

  const { data: monitoredPatients, isLoading: patientsLoading } = useMonitoredPatients();
  const { data: alerts, isLoading: alertsLoading } = useHealthAlerts();
  const { startReview, stopReview, resetReview, getSession, isEligibleForBilling } = useRpmReviewTimer();

  const criticalAlerts = alerts?.filter(a => a.type === "critical" && !a.acknowledged).length || 0;
  const warningAlerts = alerts?.filter(a => a.type === "warning" && !a.acknowledged).length || 0;
  const connectedDevices = monitoredPatients?.filter(p => p.device.connected).length || 0;
  const totalPatients = monitoredPatients?.length || 0;

  const filteredPatients = monitoredPatients?.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = 
      statusFilter === "all" ||
      (statusFilter === "critical" && patient.alerts > 0) ||
      (statusFilter === "connected" && patient.device.connected) ||
      (statusFilter === "disconnected" && !patient.device.connected);
    return matchesSearch && matchesStatus;
  }) || [];

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground">Remote Patient Monitoring</h1>
            <p className="mt-1 text-muted-foreground">
              Track real-time patient vitals and wearable device data
            </p>
          </div>

          {/* Stats Overview */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-l-4 border-l-primary">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Monitored Patients</p>
                    {patientsLoading ? (
                      <Skeleton className="h-8 w-12" />
                    ) : (
                      <p className="text-2xl font-bold">{totalPatients}</p>
                    )}
                  </div>
                  <div className="rounded-lg bg-primary/10 p-3">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-success">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Devices Connected</p>
                    {patientsLoading ? (
                      <Skeleton className="h-8 w-16" />
                    ) : (
                      <p className="text-2xl font-bold">{connectedDevices}/{totalPatients}</p>
                    )}
                  </div>
                  <div className="rounded-lg bg-success/10 p-3">
                    <Watch className="h-5 w-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-destructive">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Critical Alerts</p>
                    {alertsLoading ? (
                      <Skeleton className="h-8 w-8" />
                    ) : (
                      <p className="text-2xl font-bold text-destructive">{criticalAlerts}</p>
                    )}
                  </div>
                  <div className="rounded-lg bg-destructive/10 p-3">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-warning">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Warning Alerts</p>
                    {alertsLoading ? (
                      <Skeleton className="h-8 w-8" />
                    ) : (
                      <p className="text-2xl font-bold text-warning">{warningAlerts}</p>
                    )}
                  </div>
                  <div className="rounded-lg bg-warning/10 p-3">
                    <Bell className="h-5 w-5 text-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="alerts" className="relative">
                Alerts
                {criticalAlerts + warningAlerts > 0 && (
                  <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-xs text-destructive-foreground">
                    {criticalAlerts + warningAlerts}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="devices">Devices</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Patients</SelectItem>
                    <SelectItem value="critical">With Alerts</SelectItem>
                    <SelectItem value="connected">Connected</SelectItem>
                    <SelectItem value="disconnected">Disconnected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Patient Cards Grid */}
              {patientsLoading ? (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {[1, 2, 3, 4].map(i => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Skeleton className="h-12 w-12 rounded-full" />
                          <div>
                            <Skeleton className="h-5 w-32 mb-1" />
                            <Skeleton className="h-4 w-48" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {[1, 2, 3, 4].map(j => (
                            <Skeleton key={j} className="h-24 rounded-lg" />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredPatients.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No monitored patients</h3>
                    <p className="text-muted-foreground text-center max-w-md">
                      {searchQuery || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Patients with connected wearable devices will appear here"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {filteredPatients.map((patient) => {
                    const session = getSession(patient.memberId);
                    return (
                      <PatientVitalsCard
                        key={patient.id}
                        patient={patient}
                        onViewDetails={() => setSelectedPatient(patient.memberId)}
                        reviewTimer={{
                          elapsedSeconds: session?.elapsedSeconds || 0,
                          isActive: session?.isActive || false,
                          isEligible: isEligibleForBilling(patient.memberId),
                          onStart: () => startReview(patient.memberId, patient.name),
                          onStop: () => stopReview(patient.memberId),
                          onReset: () => resetReview(patient.memberId),
                          onClaimGenerated: () => {},
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts">
              <AlertsList />
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <VitalsTrendChart />
            </TabsContent>

            {/* Devices Tab */}
            <TabsContent value="devices">
              {patientsLoading ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map(i => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <Skeleton className="h-32" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {monitoredPatients?.map((patient) => (
                    <DeviceStatusCard key={patient.id} patient={patient} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
